import type {
  OpoBoundary,
  OpoCheck,
  OpoConfidence,
  OpoContext,
  OpoDraft,
  OpoDraftState,
  OpoGraphic,
  OpoLima,
  OpoPhase,
  OpoPlace,
  OpoRelation,
  OpoTab,
  OpoTask,
  OpoTerrainChoice,
  OpoTerrainContext,
  OpoUnit,
  OpoUnitStatus,
  OpoWeather,
  OpoWeatherView,
} from "../../api/types";
import { unitDesignation } from "../../i18n/designation";
import { k, t } from "../../i18n/i18n";
import { seasonalLabel } from "../../weather";

/** Vérification d'un OPO sans données Generex (docs/20 §12) : onglets, libellés, point suivant à vérifier et
 *  modifications du brouillon (fonctions pures, sans état ; le serveur relit tout à chaque enregistrement). */

export const TABS: readonly { value: OpoTab; label: string }[] = [
  { value: "issuer", label: k("Émetteur et niveau") },
  { value: "units", label: k("Force et unités") },
  { value: "terrain", label: k("Terrain") },
  { value: "enemy", label: k("Ennemi") },
  { value: "lima", label: k("LIMA et limites") },
  { value: "places", label: k("Lieux") },
  { value: "phases", label: k("Phases, GDH et météo") },
  { value: "missions", label: k("Missions par temps") },
];

/** Onglets où la carte d'aperçu accompagne la liste (à côté sur un grand écran, au-dessus sur un téléphone). */
export const MAP_TABS: ReadonlySet<OpoTab> = new Set(["units", "terrain", "enemy", "lima", "places", "missions"]);

export const PHASES = ["T1", "T2", "T3"] as const;
export type Phase = (typeof PHASES)[number];

export const STATUS_LABEL: Record<OpoUnitStatus, string> = {
  certain: k("Retrouvée"),
  check: k("À vérifier"),
  create: k("À créer"),
  ignore: k("Écartée"),
};

/** Statut d'une unité lue ; à l'ordre d'un S/GTIA (docs/21), une unité « à créer » est une unité reçue en
 *  renforcement, ajoutée au dossier sans toucher à l'ordre de bataille. */
export function unitStatusLabel(draft: OpoDraft, unit: OpoUnit): string {
  if (unit.status === "create" && draft.level === "COMPAGNIE" && draft.force.mode !== "create") return t("Reçue en renforcement");
  return t(STATUS_LABEL[unit.status]);
}

/** Relation de commandement d'une unité reçue par le S/GTIA : celle lue ou choisie, TACON par défaut. */
export function unitCommand(unit: OpoUnit): "TACON" | "OPCON" {
  return unit.command === "OPCON" ? "OPCON" : "TACON";
}

export const CONFIDENCE_LABEL: Record<OpoConfidence, string> = {
  certain: k("Sûr"),
  probable: k("Probable"),
  doubtful: k("Douteux"),
};

export const ROLE_LABEL: Record<string, string> = {
  reco: k("Reconnaissance"),
  ech1: k("Premier échelon"),
  ech2: k("Second échelon"),
  renforts: k("Renforts"),
  appuis: k("Appuis"),
  soutien: k("Soutien"),
};

/** Rôles proposés à la vérification d'un ordre de S/GTIA (docs/21 §4) : les emplacements du S/GTIA, nommés comme
 *  dans son ordre (l'emplacement des renforts est l'appui, la reconnaissance le pion de tête) ; pas de réserve. */
export const COMPANY_ROLE_LABEL: Record<string, string> = {
  reco: k("Pion de tête"),
  ech1: k("Premier échelon"),
  ech2: k("Second échelon"),
  renforts: k("Appui"),
};

/** Rôles proposés pour un ordre de ce niveau. */
export function roleLabels(level: string | null | undefined): Record<string, string> {
  return level === "COMPAGNIE" ? COMPANY_ROLE_LABEL : ROLE_LABEL;
}

export const LEVELS = [
  { value: "COMPAGNIE", label: k("Compagnie / Escadron (S/GTIA)") },
  { value: "REGIMENT", label: k("Régiment (GTIA)") },
  { value: "BRIGADE", label: k("Brigade") },
  { value: "DIVISION", label: k("Division") },
] as const;

/** Avertissements de l'analyse (codes du serveur), chacun montré dans l'onglet qu'il concerne. */
export const WARNING_TAB: Record<string, OpoTab> = {
  company_level: "issuer",
  issuer_as_unit: "units",
  no_units: "units",
  too_many_units: "units",
  no_phases: "phases",
  more_than_3_phases: "phases",
  no_lima: "lima",
  no_boundaries: "lima",
  boundaries_in_overlay: "lima",
  more_than_4_lima: "lima",
};

export const WARNING_LABEL: Record<string, string> = {
  company_level: k("Ordre d'une compagnie ou d'un S/GTIA : choisissez le niveau du dossier."),
  no_units: k("Aucune unité subordonnée reconnue : ajoutez-les avec « Ajouter une unité » (onglet « Force et unités »)."),
  no_phases: k("Aucune phase reconnue : les temps suivent la doctrine."),
  no_lima: k("Aucune LIMA reconnue : les LIMA suivent la doctrine."),
  no_boundaries: k("Aucune limite dans le texte : un fuseau doctrinal est proposé."),
  boundaries_in_overlay: k("Les limites sont dans un calque : joignez-le (onglet « LIMA et limites »), ou vérifiez le fuseau proposé sur la carte."),
  more_than_4_lima: k("Plus de quatre LIMA : les quatre premières règlent l'ordre, les autres sont gardées en graphiques de carte."),
  more_than_3_phases: k("Plus de trois phases : les trois premières règlent l'ordre, les autres sont gardées en graphiques de carte."),
  too_many_units: k("Beaucoup d'unités citées : vérifiez celles qui sont retenues."),
  issuer_as_unit: k("L'émetteur était cité parmi les unités subordonnées : il en est écarté."),
};

const PLACE_HOW: Record<OpoPlace["how"], string> = {
  coordinate: k("Coordonnée citée"),
  place: k("Lieu du camp"),
  clicked: k("Pointé sur la carte"),
  none: k("À pointer"),
};

/** Source d'un lieu, en clair ; sur un terrain libre, un lieu du répertoire est « Lieu du terrain » (données de la zone
 *  préparée), et un nom en attente de ce terrain « En attente du terrain ». */
export function placeHow(place: OpoPlace, zone = false, waiting = false): string {
  if (zone && place.how === "place") return t("Lieu du terrain");
  if (zone && waiting && place.how === "none" && !place.coordinate) return t("En attente du terrain");
  return t(PLACE_HOW[place.how]);
}

/** Libellé d'un lieu : son nom cité, sinon sa coordonnée. */
export function placeLabel(place: OpoPlace | undefined): string {
  if (!place) return "—";
  return place.text || place.coordinate || "—";
}

export function limaSlotLabel(lima: OpoLima): string {
  return lima.slot ?? t("Graphique de carte");
}

/** Points à vérifier par onglet (compteur des onglets) ; un onglet avec un point requis porte un point d'alerte. */
export function checksByTab(checks: OpoCheck[]): Record<OpoTab, { count: number; required: boolean }> {
  const out = Object.fromEntries(TABS.map((tab) => [tab.value, { count: 0, required: false }])) as Record<
    OpoTab,
    { count: number; required: boolean }
  >;
  for (const check of checks) {
    const entry = out[check.tab];
    if (!entry) continue;
    entry.count += 1;
    entry.required ||= check.required;
  }
  return out;
}

/** Point suivant à vérifier : le premier requis, sinon le premier tout court. */
export function nextCheck(checks: OpoCheck[]): OpoCheck | null {
  // Une attente (terrain libre en préparation) n'est pas une action : le point actionnable suivant passe devant, et
  // l'attente est dite à part (`pendingCheck`).
  const actionable = checks.filter((c) => c.kind !== "pending");
  return actionable.find((c) => c.required) ?? actionable[0] ?? pendingCheck(checks);
}

/** Point qui attend le terrain libre (préparation ou extension en cours), s'il y en a un. */
export function pendingCheck(checks: OpoCheck[]): OpoCheck | null {
  return checks.find((c) => c.kind === "pending") ?? null;
}

/** Avis de la lecture et du fuseau tiré de l'ordre (codes du serveur), onglet « LIMA et limites ». */
export const NOTICE_LABEL: Record<string, string> = {
  lima_renumbered: k("LIMA numérotées à partir de 1 : les quatre premières sont rangées en L0 à L3."),
  boundaries_cardinal: k("Limites nord, sud, est ou ouest de l'ordre : rangées en gauche et droite d'après l'axe de l'ordre."),
  lines_completed: k("LIMA sans tracé lu : posées au pas des autres LIMA de l'ordre."),
  corridor_reversed: k("L'ordre va dans l'autre sens que le fuseau automatique du camp : fuseau tracé sur l'axe de l'ordre."),
  corridor_width: k("Limites de l'ordre trop rapprochées pour ce niveau dans le camp : fuseau du camp gardé, LIMA de l'ordre posées dessus."),
  corridor_widened: k("Limites de l'ordre trop rapprochées pour ce niveau : écartées jusqu'à la largeur minimale admise."),
  overlay_missing: k("Les limites de l'ordre sont dans un calque : joignez-le, ou vérifiez le fuseau proposé."),
  phase0_initial: k("Temps 0 de l'ordre (déploiement) : ses zones donnent les positions de départ ; les temps 1 à 3 de l'ordre sont les temps T1 à T3."),
};

/** Points de structure (lecture d'un ordre d'état-major qui a pu échouer), requis tant qu'ils ne sont pas confirmés. */
export const STRUCTURE_LABEL: Record<string, string> = {
  matrix_unread: k("Matrice des missions reconnue mais non lue : vérifiez les tâches de chaque unité."),
  phases_untimed: k("Temps lus sans fenêtre de GDH : vérifiez le début et la fin de chaque temps."),
  phase_dtg_mismatch: k("GDH des temps différents entre l'idée de manœuvre et la matrice : la fenêtre qui suit l'ordre chronologique est gardée."),
  one_phase: k("Toutes les tâches lues au même temps : vérifiez la matrice des missions."),
  pages_without_text: k("Pages {pages} sans texte (numérisées) : leur contenu n'est pas lu. Vérifiez-les dans le document d'origine."),
  truncated: k("Document long lu en partie (120 pages ou 400 000 caractères au plus) : vérifiez la suite dans le document d'origine."),
};

/** Point requis qui se confirme (« C'est juste ») : structure de l'ordre, unité suspecte. */
export function confirmable(check: OpoCheck): boolean {
  return !check.required || check.kind === "structure" || check.kind === "suspect";
}

const RELATION_LABEL: Record<string, string> = {
  AD: k("appui direct"),
  AE: k("action d'ensemble"),
  kept: k("conservé aux ordres"),
  detached: k("détaché"),
  neighbour: k("voisin"),
  higher: k("échelon supérieur"),
};
const SIDE_LABEL: Record<string, string> = {
  ouest: k("à l'ouest"),
  est: k("à l'est"),
  nord: k("au nord"),
  sud: k("au sud"),
  second: k("en deuxième échelon"),
  coordination: k("en coordination"),
};

/** Ce qu'un point à vérifier désigne, en clair. */
export function checkLabel(check: OpoCheck, draft: OpoDraft): string {
  const unit = (id: string) => draft.units.find((u) => u.id === id);
  const place = draft.places.find((p) => p.id === check.id);
  if (check.kind === "notice" && NOTICE_LABEL[check.id]) return t(NOTICE_LABEL[check.id]!);
  if (check.kind === "structure" && STRUCTURE_LABEL[check.id]) return t(STRUCTURE_LABEL[check.id]!, { pages: check.detail ?? "" });
  if (check.kind === "relation") {
    const relation = (draft.relations ?? []).find((r) => r.id === check.id);
    return t("Rattachement ou appui lu à vérifier : {text}", { text: relation ? relationText(relation) : check.id });
  }
  const lima = draft.lima.find((l) => l.id === check.id);
  if (lima && check.tab === "lima") return t("LIMA « {name} » proposée par l'IA à valider", { name: lima.name });
  if (check.id.startsWith("phase") && check.tab === "phases") return t("Phases proposées par l'IA à valider");
  if (check.tab === "terrain") {
    const terrain = terrainCheckLabel(check);
    if (terrain) return terrain;
  }
  switch (check.id) {
    case "weather":
      return t("Météo de l'ordre à reprendre ou non");
    case "weather_terrain":
      return t("Météo de l'ordre changée par le terrain : à confirmer");
    case "weather_unread":
      return t(WEATHER_NOT_READ);
    case "no_units":
      return t("Aucune unité amie : ajoutez au moins une unité");
    case "issuer_proposal":
      return t("Proposition de l'IA pour l'émetteur");
    case "corridor_llm":
      return t("Limites proposées par l'IA à valider");
    case "h_hour":
      return t("Heure H à préciser : des LIMA s'y réfèrent");
    case "level":
      return t("Niveau de l'ordre à choisir");
    case "camp":
      return t("Camp à choisir");
    case "force":
      return t("Force à choisir");
    case "mission":
      return t("Mission du niveau à choisir");
    case "issuer":
      return t("Émetteur à confirmer");
    case "start":
      return t("Début de l'exercice à saisir");
    case "corridor":
      return t("Fuseau à reprendre sur la carte");
  }
  if (place) {
    if (check.kind === "missing") return t("Lieu « {name} » à pointer sur la carte", { name: placeLabel(place) });
    if (check.kind === "outside") return t("Lieu « {name} » hors du fuseau de l'ordre : à confirmer", { name: placeLabel(place) });
    return t("Lieu « {name} » à confirmer", { name: placeLabel(place) });
  }
  const [id, phase] = check.id.split(":");
  const found = unit(id ?? "");
  if (found && phase) {
    const args = { unit: unitDesignation(found.designation), phase };
    switch (check.kind) {
      case "mapped":
        return t("{unit} en {phase} : mission la plus proche à confirmer", args);
      case "llm":
        return t("{unit} en {phase} : tâche proposée par l'IA à valider", args);
      case "unmapped":
        return t("{unit} en {phase} : tâche sans mission sur la carte, à choisir", args);
      case "notapplied":
        return t("{unit} en {phase} : la carte ne reprend pas la mission de l'ordre", args);
      case "empty":
        return t("{unit} en {phase} : aucune tâche lue à ce temps", args);
      default:
        return t("{unit} en {phase} : temps à confirmer", args);
    }
  }
  if (found && check.kind === "llm") return t("{unit} : unité proposée par l'IA à valider", { unit: unitDesignation(found.designation) });
  if (found && check.kind === "suspect")
    return t("{unit} : émetteur, voisin ou ennemi rangé parmi les unités amies, à écarter ou à confirmer", { unit: unitDesignation(found.designation) });
  if (found) return t("{unit} : unité à vérifier", { unit: unitDesignation(found.designation) });
  return t("Point à vérifier");
}

/** Points de l'onglet « Terrain » d'un ordre hors des camps (docs/20 §14.2), en clair. */
function terrainCheckLabel(check: OpoCheck): string | null {
  switch (check.id) {
    case "zone":
      return check.kind === "pending"
        ? t("Terrain libre en préparation : la création du dossier attend qu'il soit prêt")
        : check.kind === "failed"
          ? t("Préparation du terrain libre en échec : relancez-la")
          : t("Terrain libre à préparer d'après l'ordre");
    case "zone_area":
      return t("Terrain à situer : pointez un repère ou tracez l'axe de la manœuvre sur la carte");
    case "grid_zone":
      return t("Zone de grille déduite des coordonnées écrites sans zone : à confirmer");
    case "extend":
      return check.kind === "pending"
        ? t("Extension du terrain libre en préparation")
        : t("Fuseau de l'ordre hors du terrain préparé : étendez le terrain, ou confirmez");
    default:
      return null;
  }
}

// --- Météo de l'ordre (chantier de cohérence météo du 30/10/2026) ---------------------------------------------

export type { OpoWeather, OpoWeatherView };

/** Avis d'une météo lue mais non reprise (aussi celui du serveur à la création du dossier). */
export const WEATHER_NOT_READ = k("Météo de l'ordre non reprise : météo de saison du terrain, modifiable dans les Données initiales.");

/** Météo lue dans l'ordre (absente d'un import ouvert avant sa lecture : le serveur la lit à la relecture). */
export function weatherOf(draft: OpoDraft): OpoWeather | null {
  return draft.weather ?? null;
}

export function weatherView(context: OpoContext): OpoWeatherView {
  return context.weather ?? { found: false };
}

/** Choix de la vérification : la météo de l'ordre reprise dans le dossier, ou la météo de saison gardée. */
export function withWeatherTaken(draft: OpoDraft, take: boolean): OpoDraft {
  const weather = weatherOf(draft);
  return weather ? { ...draft, weather: { ...weather, take } } : draft;
}

/** La météo de l'ordre est-elle encore à reprendre ou non (lecture probable, rien de décidé) ? */
export function weatherUndecided(draft: OpoDraft, context: OpoContext): boolean {
  const weather = weatherOf(draft);
  const view = weatherView(context);
  return Boolean(weather?.found && view.label && view.state !== "unread" && weather.take == null && weather.confidence !== "certain");
}

/** Libellé d'une météo composé par le serveur : « auto » est la météo de saison. */
export function weatherLabel(label: string | undefined): string {
  if (!label) return "—";
  return label === "auto" ? seasonalLabel() : label;
}

/** Ce que devient la météo de l'ordre dans le dossier, en clair. */
export function weatherOutcome(view: OpoWeatherView): string {
  switch (view.state) {
    case "unread":
      return t(WEATHER_NOT_READ);
    case "declined":
      return t("Météo de saison du terrain, modifiable dans les Données initiales.");
    case "pending":
      return t("Ramenée au terrain dès qu'il sera prêt.");
    case "adjusted":
      return t("Ramenée au terrain : vent ou température repris de la saison.");
    case "impossible":
      return t("Impossible sur ce terrain à cette date : météo de saison.");
    case "kept":
      return view.applied_label === "auto" ? t("Météo de saison du terrain, comme l'ordre.") : t("Reprise dans le dossier, modifiable dans les Données initiales.");
    default:
      return "";
  }
}

// --- Terrain libre d'un ordre hors des camps (docs/20 §14) ------------------------------------------------------

/** Avis d'une action sur le terrain de l'import (codes du serveur) ; « lancée » n'est pas montré : l'encadré de la
 *  préparation le dit déjà (`QUIET_NOTICES`). */
export const TERRAIN_NOTICE: Record<string, string> = {
  terrain_queued: k("Préparation du terrain lancée : vous pouvez continuer la vérification, la création attend qu'il soit prêt."),
  terrain_reused: k("Terrain de votre licence repris : aucune préparation n'est nécessaire."),
  terrain_extending: k("Extension du terrain lancée : la vérification continue, la création du dossier attend qu'elle se termine."),
  terrain_released: k("Terrain retiré de l'import : il reste dans vos terrains libres."),
  terrain_cancelled: k("Préparation annulée et terrain retiré de l'import : ajustez l'emprise, puis relancez la préparation."),
};

export const QUIET_NOTICES = new Set(["terrain_queued"]);

/** Avis de l'emprise proposée (codes du serveur). */
export const TERRAIN_NOTE: Record<string, string> = {
  terrain_shortened: k("Manœuvre plus longue que le terrain admis à ce niveau : l'emprise est raccourcie autour de son centre."),
  terrain_narrow: k("Des lieux de l'ordre débordent sur les côtés de l'emprise : le terrain pourra être étendu une fois prêt."),
};

/** Origine de l'axe de l'emprise proposée. */
export const AXIS_SOURCE: Record<string, string> = {
  corridor: k("d'après le fuseau de l'ordre"),
  order: k("d'après l'axe de l'ordre"),
  sides: k("des unités amies vers l'ennemi"),
  spread: k("d'après la répartition des lieux de l'ordre"),
  drawn: k("tracé ou ajusté sur la carte"),
  point: k("autour du repère pointé"),
};

/** Ce qui a donné la zone de grille des coordonnées écrites sans zone. */
export const INFERENCE_SOURCE: Record<string, string> = {
  anchor: k("Repère pointé sur la carte"),
  coordinates: k("Coordonnées complètes de l'ordre"),
  mention: k("Zone de grille écrite dans l'ordre"),
  prior: k("Carrés de 100 km reconnus près des terrains habituels"),
};

/** Nature d'un graphique nommé lu dans le texte. */
/** Choix d'un graphique nommé (masculin : « Tracé sur la carte », « Écarté » ; une limite est féminine). */
export const GRAPHIC_KEEP: Record<"keep" | "ignore", string> = { keep: k("Tracé sur la carte"), ignore: k("Écarté") };
export const BOUNDARY_KEEP: Record<"keep" | "ignore", string> = { keep: k("Tracée sur la carte"), ignore: k("Écartée") };

export const GRAPHIC_KIND: Record<OpoGraphic["kind"], string> = {
  ea: k("Zone d'engagement"),
  route: k("Itinéraire"),
  axis: k("Axe"),
  obj: k("Objectif"),
  nai: k("Zone d'intérêt"),
  bp: k("Position de bataille"),
};

const NO_TERRAIN: OpoTerrainChoice = { mode: "camp", chosen: false, anchor: null, axis: null };

/** Choix du terrain du brouillon (valeur par défaut d'un import d'avant l'étape 4). */
export function terrainOf(draft: OpoDraft): OpoTerrainChoice {
  return draft.terrain ?? NO_TERRAIN;
}

export function withTerrain(draft: OpoDraft, patch: Partial<OpoTerrainChoice>): OpoDraft {
  return { ...draft, terrain: { ...terrainOf(draft), ...patch } };
}

/** L'import est-il sur un terrain libre (choisi, ou tiré d'un ordre dont aucun camp ne contient les lieux) ? */
export function onFreeTerrain(state: Pick<OpoDraftState, "context">): boolean {
  return state.context.terrain?.mode === "zone";
}

/** Terrain libre en file, en préparation ou en extension : la vérification continue, la zone est relevée. */
export function terrainWaiting(terrain: OpoTerrainContext | undefined): boolean {
  return terrain?.mode === "zone" && ["queued", "preparing", "extending"].includes(terrain.state);
}

/** Le terrain de l'import porte-t-il les lieux et le fuseau (camp choisi, ou terrain libre prêt) ? */
export function terrainReady(state: Pick<OpoDraftState, "draft" | "context">): boolean {
  if (!onFreeTerrain(state)) return Boolean(state.draft.camp);
  return Boolean(state.context.map.terrain?.ready);
}

/** Action attendue de l'onglet « Terrain » pour l'état du terrain libre : elle y est désignée (et « Voir » ne l'est
 *  plus quand l'onglet est ouvert). */
export type TerrainAction = "level" | "area" | "confirm" | "prepare" | "retry" | "extend";
export function terrainAction(terrain: OpoTerrainContext | undefined): TerrainAction | null {
  if (terrain?.mode !== "zone") return null;
  switch (terrain.state) {
    case "needs_level":
      return "level";
    case "needs_area":
      return "area";
    case "confirm_grid":
      return "confirm";
    case "proposed":
      return terrain.proposal?.error ? "area" : "prepare";
    case "failed":
      return "retry";
    case "extend":
      return "extend";
    default:
      return null;
  }
}

/** Axe de l'emprise : celui tracé ou ajusté sur la carte, sinon celui que le serveur propose. */
export function terrainAxis(state: Pick<OpoDraftState, "draft" | "context">): { start: [number, number]; end: [number, number] } | null {
  return terrainOf(state.draft).axis ?? state.context.terrain?.proposal?.axis ?? null;
}

const round7 = (p: [number, number]): [number, number] => [Math.round(p[0] * 1e6) / 1e6, Math.round(p[1] * 1e6) / 1e6];

/** Extrémité de l'axe déplacée (arrière, avant), ou axe entier déplacé par son milieu. */
export function movedAxis(
  axis: { start: [number, number]; end: [number, number] },
  handle: "start" | "end" | "middle",
  point: [number, number],
): { start: [number, number]; end: [number, number] } {
  if (handle === "start") return { start: round7(point), end: axis.end };
  if (handle === "end") return { start: axis.start, end: round7(point) };
  const mid: [number, number] = [(axis.start[0] + axis.end[0]) / 2, (axis.start[1] + axis.end[1]) / 2];
  const dx = point[0] - mid[0];
  const dy = point[1] - mid[1];
  return { start: round7([axis.start[0] + dx, axis.start[1] + dy]), end: round7([axis.end[0] + dx, axis.end[1] + dy]) };
}

/** Libellé d'une limite entre unités : « 1er RI / 503e RCC », « Limite gauche du 1er RI ». */
export function boundaryLabel(boundary: OpoBoundary): string {
  const [a, b] = boundary.labels;
  if (boundary.side === "left") return t("Limite gauche de {unit}", { unit: a ?? "—" });
  if (boundary.side === "right") return t("Limite droite de {unit}", { unit: a ?? "—" });
  return t("Limite {a} / {b}", { a: a ?? "—", b: b ?? "—" });
}

export function withBoundary(draft: OpoDraft, id: string, patch: Partial<OpoBoundary>): OpoDraft {
  return { ...draft, boundaries: (draft.boundaries ?? []).map((b) => (b.id === id ? { ...b, ...patch, confidence: "certain" } : b)) };
}

export function withGraphic(draft: OpoDraft, id: string, patch: Partial<OpoGraphic>): OpoDraft {
  return { ...draft, graphics: (draft.graphics ?? []).map((g) => (g.id === id ? { ...g, ...patch, confidence: "certain" } : g)) };
}

/** Points résolus des lieux d'un graphique nommé, dans l'ordre cité. */
export function graphicPoints(draft: OpoDraft, graphic: Pick<OpoGraphic, "places">): [number, number][] {
  const byId = new Map(draft.places.map((p) => [p.id, p.point]));
  return graphic.places.flatMap((id) => {
    const at = byId.get(id);
    return at ? [at] : [];
  });
}

/** Rattachement ou appui lu, en clair (« 1er RCP + 1 Cie du 2e REP », « 17e RGP · OPCON · 2e REP »). */
export function relationText(relation: OpoRelation): string {
  if (relation.change === "+" || relation.change === "-") return `${relation.unit} ${relation.change} ${relation.text}`;
  const kind = RELATION_LABEL[relation.relation] ? t(RELATION_LABEL[relation.relation]!) : relation.relation;
  const to = SIDE_LABEL[relation.to] ? t(SIDE_LABEL[relation.to]!) : relation.to;
  return [relation.unit, kind, to].filter(Boolean).join(" · ");
}

/** Point à vérifier qu'une ligne porte (ancre `data-check`), s'il y en a un. */
export function checkAt(checks: OpoCheck[], anchor: string): OpoCheck | undefined {
  return checks.find((c) => checkAnchor(c) === anchor);
}

/** Point vérifié à la main : il n'est plus compté (les points requis ne se confirment pas). */
export function confirm(draft: OpoDraft, check: OpoCheck): OpoDraft {
  const anchor = checkAnchor(check);
  const confirmed = draft.confirmed ?? [];
  return confirmed.includes(anchor) ? draft : { ...draft, confirmed: [...confirmed, anchor] };
}

/** Proposition de l'IA validée : elle devient une valeur de l'utilisateur. */
export function acceptProposal(draft: OpoDraft, check: OpoCheck): OpoDraft {
  const mine = { origin: "user", confidence: "certain" as const };
  if (check.id === "corridor_llm") return { ...draft, corridor: { ...draft.corridor, proposed: false } };
  if (check.tab === "phases") return { ...draft, phases: draft.phases.map((p) => (p.origin === "llm" ? { ...p, ...mine } : p)) };
  if (check.tab === "lima") return withLima(draft, check.id, { ...mine, llm: "" });
  const [id, phase] = check.id.split(":");
  if (phase) {
    return {
      ...draft,
      units: draft.units.map((u) => {
        const task = u.tasks[phase as Phase];
        return u.id === id && task ? { ...u, tasks: { ...u.tasks, [phase]: { ...task, ...mine } } } : u;
      }),
    };
  }
  return withUnit(draft, check.id, mine);
}

/** Proposition de l'IA écartée : retirée du brouillon (une valeur lue par règles reste). */
export function rejectProposal(draft: OpoDraft, check: OpoCheck): OpoDraft {
  if (check.id === "corridor_llm") return { ...draft, corridor: { ...draft.corridor, proposed: false, left_text: [], right_text: [] } };
  if (check.tab === "phases") return { ...draft, phases: draft.phases.filter((p) => p.origin !== "llm") };
  if (check.tab === "lima") {
    const lima = draft.lima.find((l) => l.id === check.id);
    if (lima?.llm === "dtg") return { ...draft, lima: draft.lima.map((l) => (l.id === lima.id ? { ...l, dtg: "", llm: "" } : l)) };
    return { ...draft, lima: draft.lima.filter((l) => l.id !== check.id) };
  }
  const [id, phase] = check.id.split(":");
  if (phase) {
    return {
      ...draft,
      units: draft.units.map((u) => {
        if (u.id !== id) return u;
        const tasks = { ...u.tasks };
        delete tasks[phase as Phase];
        return { ...u, tasks };
      }),
    };
  }
  return { ...draft, units: draft.units.filter((u) => u.id !== check.id) };
}

/** Unité ajoutée à la main (l'ordre la cite, la lecture l'a manquée) : rapprochée de l'ordre de bataille par le
 *  serveur, ou créée avec lui ; ses missions se choisissent dans « Missions par temps ». */
export function addUnit(draft: OpoDraft, designation: string): OpoDraft {
  const name = designation.trim().slice(0, 120);
  if (!name) return draft;
  let n = draft.units.length + 1;
  while (draft.units.some((u) => u.id === `a${n}`)) n += 1;
  const unit: OpoUnit = {
    id: `a${n}`, side: "AMI", designation: name, echelon: null, kind: null, status: "check", target: null,
    chosen: false, role: null, role_chosen: false, effort: false, sector: "", tasks: {}, extra: [], contingent: [],
    place: null, relation: "", command: null, command_chosen: false, confidence: "certain", origin: "user", source: null,
  };
  return { ...draft, units: [...draft.units, unit] };
}

/** Début ou fin d'une phase corrigé à la vérification. */
export function withPhase(draft: OpoDraft, index: number, patch: Partial<OpoPhase>): OpoDraft {
  return {
    ...draft,
    phases: draft.phases.map((p) => (p.index === index ? { ...p, ...patch, origin: "user", confidence: "certain" } : p)),
  };
}

/** Distance et direction d'un point à un autre, en clair (« 2,1 km au nord-est »). */
export function bearingLabel(from: [number, number], to: [number, number]): string {
  const lat = ((from[1] + to[1]) / 2) * (Math.PI / 180);
  const dx = (to[0] - from[0]) * 111.32 * Math.cos(lat);
  const dy = (to[1] - from[1]) * 110.57;
  const km = Math.hypot(dx, dy);
  const angle = ((Math.atan2(dx, dy) * 180) / Math.PI + 360) % 360;
  const names = [k("au nord"), k("au nord-est"), k("à l'est"), k("au sud-est"), k("au sud"), k("au sud-ouest"), k("à l'ouest"), k("au nord-ouest")];
  const direction = t(names[Math.round(angle / 45) % 8]!);
  return t("{km} km {direction}", { km: km.toLocaleString(undefined, { maximumFractionDigits: 1 }), direction });
}

/** Repère d'un candidat (homonyme) : distance et direction depuis le lieu de l'ordre le plus proche qui n'est pas
 *  ce nom (« Le Camp — 2,1 km au nord-est de Suippes »). */
export function candidateLabel(draft: OpoDraft, place: OpoPlace, candidate: { name: string; point: [number, number] }): string {
  let best: { name: string; d: number; point: [number, number] } | null = null;
  for (const other of draft.places) {
    if (other.id === place.id || !other.point || other.confidence === "doubtful") continue;
    const d = Math.hypot(other.point[0] - candidate.point[0], other.point[1] - candidate.point[1]);
    if (!best || d < best.d) best = { name: placeLabel(other), d, point: other.point };
  }
  if (!best) return candidate.name;
  return t("{name} — {where} de {ref}", { name: candidate.name, where: bearingLabel(best.point, candidate.point), ref: best.name });
}

/** Attribut `data-check` du champ d'un point à vérifier (désigné à l'utilisateur). */
export function checkAnchor(check: OpoCheck): string {
  return `${check.tab}:${check.id}`;
}

// --- Modifications du brouillon -----------------------------------------------------------------------------

/** Unité corrigée à la vérification : elle devient une valeur de l'utilisateur (plus douteuse ni proposée). */
export function withUnit(draft: OpoDraft, id: string, patch: Partial<OpoUnit>): OpoDraft {
  return {
    ...draft,
    units: draft.units.map((u) => (u.id === id ? { ...u, origin: "user", confidence: "certain", ...patch } : u)),
  };
}

export function withTask(draft: OpoDraft, id: string, phase: Phase, patch: Partial<OpoTask>): OpoDraft {
  return {
    ...draft,
    units: draft.units.map((u) => {
      if (u.id !== id) return u;
      const current: OpoTask = u.tasks[phase] ?? {
        verb: "", code: null, mission: null, mapped: false, place: null, lima: "", facing: "",
        confidence: "probable", origin: "user", source: null,
      };
      return { ...u, tasks: { ...u.tasks, [phase]: { ...current, ...patch, origin: "user", confidence: "certain" } } };
    }),
  };
}

export function withPlace(draft: OpoDraft, id: string, patch: Partial<OpoPlace>): OpoDraft {
  return { ...draft, places: draft.places.map((p) => (p.id === id ? { ...p, ...patch } : p)) };
}

/** Lieu pointé d'un clic sur la carte (il garde ce point, quel que soit le camp). */
export function pointed(draft: OpoDraft, id: string, point: [number, number]): OpoDraft {
  const at: [number, number] = [Math.round(point[0] * 1e7) / 1e7, Math.round(point[1] * 1e7) / 1e7];
  return withPlace(draft, id, { point: at, how: "clicked", confidence: "certain", candidates: [], precision_m: null });
}

/** LIMA corrigée à la vérification : elle devient une valeur de l'utilisateur (plus douteuse ni proposée). */
export function withLima(draft: OpoDraft, id: string, patch: Partial<OpoLima>): OpoDraft {
  return {
    ...draft,
    lima: draft.lima.map((l) => (l.id === id ? { ...l, origin: "user", confidence: "certain", llm: "", ...patch } : l)),
  };
}

/** Sommets d'une limite proposés au tracé : au plus `max`, régulièrement espacés, extrémités comprises (une limite
 *  adaptée au terrain en compte des dizaines). */
export function handleIndices(count: number, max = 6): number[] {
  if (count <= max) return Array.from({ length: count }, (_, i) => i);
  return [...new Set(Array.from({ length: max }, (_, i) => Math.round((i * (count - 1)) / (max - 1))))];
}

/** Sommet déplacé d'une limite (gauche ou droite) ou extrémité d'une LIMA : le fuseau devient celui tracé à la
 *  main (déplaçable ensuite à nouveau) ; une limite dense est d'abord réduite aux sommets proposés au tracé
 *  (`handleIndices`), le sommet déplacé compris. */
export function movedVertex(
  draft: OpoDraft,
  target: { rail: "left" | "right"; index: number } | { line: "L0" | "L1" | "L2" | "L3"; index: number },
  point: [number, number],
): OpoDraft {
  const corridor = { ...draft.corridor, mode: "custom" as const, origin: "drawn" as const, chosen: true };
  if ("rail" in target) {
    const rail = corridor[target.rail];
    const kept = handleIndices(rail.length);
    corridor[target.rail] = kept.map((i) => (i === target.index ? point : rail[i]!));
  } else {
    const line = [...(corridor.lines[target.line] ?? [])];
    line[target.index] = point;
    corridor.lines = { ...corridor.lines, [target.line]: line };
  }
  return { ...draft, corridor };
}

/** Fuseau du camp rétabli (le fuseau automatique du dossier, à la largeur doctrinale). */
export function campCorridor(draft: OpoDraft): OpoDraft {
  return { ...draft, corridor: { ...draft.corridor, mode: "camp", origin: "camp", chosen: true } };
}

/** Unités d'un camp, par côté, dans l'ordre du brouillon. */
export function unitsOf(draft: OpoDraft, side: "AMI" | "ENI"): OpoUnit[] {
  return draft.units.filter((u) => u.side === side);
}

/** Libellé de la cible d'une unité (unité du dispositif), « — » sans cible. */
export function targetLabel(context: OpoContext, key: string | null): string {
  if (!key) return "—";
  return context.targets.find((t) => t.key === key)?.label ?? key;
}

/** Valeur d'un brouillon pour la liste déroulante des correspondances : cible, « create » ou « ignore ». */
export function matchValue(unit: OpoUnit): string {
  if (unit.status === "ignore") return "ignore";
  if (unit.status === "create") return "create";
  return unit.target ?? "";
}

/** Choix d'une correspondance à la vérification : unité du dispositif, à créer, ou écartée. */
export function chooseMatch(draft: OpoDraft, unit: OpoUnit, value: string): OpoDraft {
  if (value === "ignore") return withUnit(draft, unit.id, { status: "ignore", target: null, chosen: true });
  if (value === "create") return withUnit(draft, unit.id, { status: "create", target: null, chosen: true });
  return withUnit(draft, unit.id, { status: "certain", target: value || null, chosen: true });
}
